import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseISO, format } from 'date-fns';

import Loading from "../../../components/Loading";
import Data from '../../../../repository';
import { createDiscountCodeSchema } from '../../../../validation/schemas';

import styles from '../../../../styles/Admin.module.scss';

function CreateDiscountCodeForm() {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [serverError, setServerError] = useState(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		control
	} = useForm({
		resolver: yupResolver(createDiscountCodeSchema),
		defaultValues: {
			name: '',
			code: '',
			discountType: 'percentage',
			percentageOff: '',
			amountOff: '',
			start: '',
			end: '',
			enabled: true
		}
	});

	const discountType = useWatch({ control, name: 'discountType' });

	const onSubmit = async (data) => {
		setLoading(true);
		setServerError(null);

		try {
			await Data.createDiscountCode({
				name: data.name,
				code: data.code,
				discountType: data.discountType,
				...(data.discountType === 'percentage' ? { percentageOff: Number(data.percentageOff) } : { amountOff: Math.round(Number(data.amountOff) * 100) }),
				start: data.start,
				end: data.end,
				enabled: data.enabled === 'true' || data.enabled === true
			});
			navigate('/admin/discount-codes');
		} catch (error) {
			setServerError(error.response?.data?.message || 'Failed to create discount code. Please try again.');
			setLoading(false);
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div>
				<p>Name: *</p>
				<input
					type="text"
					{...register('name')}
					className={errors.name ? 'input-error' : ''}
				/>
				{errors.name && <span className="form-field-error">{errors.name.message}</span>}
			</div>

			<div>
				<p>Code: *</p>
				<input
					type="text"
					{...register('code')}
					className={errors.code ? 'input-error' : ''}
				/>
				{errors.code && <span className="form-field-error">{errors.code.message}</span>}
			</div>

			<div>
				<p>Discount Type: *</p>
				<select {...register('discountType')}>
					<option value="percentage">Percentage Off</option>
					<option value="amount">Amount Off</option>
				</select>
				{errors.discountType && <span className="form-field-error">{errors.discountType.message}</span>}
			</div>

			{discountType === 'percentage' && (
				<div>
					<p>Percentage Off: *</p>
					<input
						type="number"
						{...register('percentageOff')}
						min={0}
						max={100}
						step={1}
						className={errors.percentageOff ? 'input-error' : ''}
					/>
					{errors.percentageOff && <span className="form-field-error">{errors.percentageOff.message}</span>}
				</div>
			)}

			{discountType === 'amount' && (
				<div>
					<p>Amount Off (£): *</p>
					<input
						type="number"
						{...register('amountOff')}
						min={0}
						step={0.01}
						className={errors.amountOff ? 'input-error' : ''}
					/>
					{errors.amountOff && <span className="form-field-error">{errors.amountOff.message}</span>}
				</div>
			)}

			<div>
				<p>Valid From: *</p>
				<Controller
					name="start"
					control={control}
					render={({ field }) => (
						<DatePicker
							value={field.value ? parseISO(field.value) : null}
							onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
							slotProps={{ textField: { className: errors.start ? 'input-error' : '' } }}
						/>
					)}
				/>
				{errors.start && <span className="form-field-error">{errors.start.message}</span>}
			</div>

			<div>
				<p>Valid Until: *</p>
				<Controller
					name="end"
					control={control}
					render={({ field }) => (
						<DatePicker
							value={field.value ? parseISO(field.value) : null}
							onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
							slotProps={{ textField: { className: errors.end ? 'input-error' : '' } }}
						/>
					)}
				/>
				{errors.end && <span className="form-field-error">{errors.end.message}</span>}
			</div>

			<div>
				<p>Status:</p>
				<select {...register('enabled')}>
					<option value="true">Enabled</option>
					<option value="false">Disabled</option>
				</select>
			</div>

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<button type="submit" className="btn btnPrimary" disabled={loading}>
				{loading ?
					<Loading button={true} />
					:
					`Create Discount Code`
				}
			</button>
		</form>
	)
}

export default CreateDiscountCodeForm;
